// src/utils/blankPageDiagnostics.js
/**
 * Diagnostic instrumentation for diagnosing intermittent blank page / white screen issues.
 * Captures lifecycle events, unhandled errors, auth state changes, 401 responses, WebSockets, and chunk errors.
 * 
 * IMPORTANT: Strictly diagnostic. Does NOT alter business logic, tokens, or network flows.
 */

import logger from "./logger";

const DIAG_PREFIX = "[BLANK_PAGE_DIAGNOSTIC]";

const getSafePathname = () => {
  try {
    return typeof window !== "undefined" ? window.location.pathname + window.location.search : "unknown";
  } catch {
    return "unknown";
  }
};

const getTimestamp = () => new Date().toISOString();

export const logLifecycleDiagnostic = (eventName, extra = {}) => {
  // Lifecycle diagnostic - debug logging omitted
};

export const logAuthDiagnostic = (action, metadata = {}) => {
  try {
    const sanitizedMetadata = { ...metadata };
    delete sanitizedMetadata.token;
    delete sanitizedMetadata.access_token;
    delete sanitizedMetadata.refresh_token;
    delete sanitizedMetadata.password;
    delete sanitizedMetadata.headers;
    delete sanitizedMetadata.authorization;
    delete sanitizedMetadata.Authorization;

    logger.debug(`${DIAG_PREFIX}[AUTH]`, {
      action,
      pathname: getSafePathname(),
      timestamp: getTimestamp(),
      ...sanitizedMetadata,
    });
  } catch (_) {}
};

export const logHttp401Diagnostic = (details = {}) => {
  try {
    const sanitizedDetails = { ...details };
    delete sanitizedDetails.token;
    delete sanitizedDetails.access_token;
    delete sanitizedDetails.refresh_token;
    delete sanitizedDetails.headers;
    delete sanitizedDetails.authorization;
    delete sanitizedDetails.Authorization;

    logger.warn(`${DIAG_PREFIX}[HTTP_401]`, {
      pathname: getSafePathname(),
      timestamp: getTimestamp(),
      ...sanitizedDetails,
    });
  } catch (_) {}
};

export const logChunkDiagnostic = (details = {}) => {
  try {
    logger.error(`${DIAG_PREFIX}[CHUNK]`, {
      pathname: getSafePathname(),
      timestamp: getTimestamp(),
      ...details,
    });
  } catch (_) {}
};

export const logWsDiagnostic = (name, event, metadata = {}) => {
  // WS diagnostic - debug logging omitted
};

export const logRouteDiagnostic = (action, metadata = {}) => {
  // Route diagnostic - debug logging omitted
};

export function installBlankPageDiagnostics() {
  if (typeof window === "undefined") return;
  if (window.__BLANK_PAGE_DIAGNOSTICS_INSTALLED__) return;
  window.__BLANK_PAGE_DIAGNOSTICS_INSTALLED__ = true;

  logger.debug(`${DIAG_PREFIX}[INIT] Diagnostic logging installed at ${getTimestamp()}`);

  // 1. JavaScript runtime errors
  window.addEventListener("error", (event) => {
    try {
      const errorMsg = event.message || "";
      const isChunk =
        /chunkloaderror|loading chunk|failed to fetch dynamically imported module|importing a module script failed/i.test(
          errorMsg
        ) || (event.error && /chunkloaderror/i.test(event.error?.name || ""));

      if (isChunk) {
        logChunkDiagnostic({
          source: "window.onerror",
          message: event.message,
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
          stack: event.error?.stack || null,
        });
      }

      logger.error(`${DIAG_PREFIX}[ERROR]`, {
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        stack: event.error?.stack || null,
        pathname: getSafePathname(),
        timestamp: getTimestamp(),
        isChunkError: isChunk,
      });
    } catch (_) {}
  });

  // 2. Unhandled Promise rejections
  window.addEventListener("unhandledrejection", (event) => {
    try {
      const reason = event.reason;
      const reasonMsg =
        typeof reason === "object" && reason !== null
          ? reason.message || String(reason)
          : String(reason);
      const isChunk =
        /chunkloaderror|loading chunk|failed to fetch dynamically imported module|importing a module script failed/i.test(
          reasonMsg
        ) || (reason && typeof reason === "object" && reason.name === "ChunkLoadError");

      if (isChunk) {
        logChunkDiagnostic({
          source: "unhandledrejection",
          reason: reasonMsg,
          stack: reason?.stack || null,
        });
      }

      logger.error(`${DIAG_PREFIX}[UNHANDLED_REJECTION]`, {
        reason: reasonMsg,
        stack: reason?.stack || null,
        pathname: getSafePathname(),
        timestamp: getTimestamp(),
        isChunkError: isChunk,
      });
    } catch (_) {}
  });

  // 3. Browser lifecycle events
  const lifecycleEvents = [
    "visibilitychange",
    "focus",
    "blur",
    "pageshow",
    "pagehide",
    "online",
    "offline",
  ];

  lifecycleEvents.forEach((evtName) => {
    const target = evtName === "visibilitychange" ? document : window;
    target.addEventListener(evtName, () => {
      logLifecycleDiagnostic(evtName);
    }, { passive: true });
  });
}
